import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { homeworkAPI, moduleMaterialsAPI, examComponentsAPI } from '../../services/api';
import PDFViewerModal from '../../components/PDFViewerModal';
import { translateLocation } from '../../utils/translationUtils';
import './MyCourses.css';

const EXAM_EVENT_TYPES = new Set([
  'exam',
  'resit',
  'bp-submission',
  'so-submission',
  'adviesrapport-submission',
  'presentation',
  // Legacy values still accepted for previously stored calendar events.
  'bp-so-submission',
  'report-submission',
  'report-review'
]);
const EXAM_UPLOAD_TYPES = new Set(['beroepsproduct', 'adviesrapport', 'schriftelijke-opdracht']);

const examTypeLabel = (value) => {
  const labels = {
    kennistentamen: 'Kennistentamen',
    'onderzoeksverslag-voorstel': 'Onderzoeksverslag/Voorstel',
    beroepsproduct: 'Beroepsproduct',
    adviesrapport: 'Adviesrapport',
    'reflectieverslag-portfolio': 'Reflectieverslag/Portfolio',
    'schriftelijke-opdracht': 'Schriftelijke opdracht',
    presentatievorm: 'Presentatievorm'
  };
  return labels[value] || value || '-';
};

const MyCourses = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getStudentEnrollments, getCourseById, getStudentGrades, getStudentHomeworkSubmissions, homework = [], studies = [], data } = useData();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState('');
  const [homeworkSubmissionsMap, setHomeworkSubmissionsMap] = useState({});
  const [homeworkUploading, setHomeworkUploading] = useState(false);
  const [homeworkSelectedFiles, setHomeworkSelectedFiles] = useState({});
  const [homeworkComments, setHomeworkComments] = useState({});
  const [homeworkSubmissionModes, setHomeworkSubmissionModes] = useState({});
  const [homeworkTextSubmissions, setHomeworkTextSubmissions] = useState({});
  const [homeworkError, setHomeworkError] = useState('');
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' });
  const [examComponentItems, setExamComponentItems] = useState([]);
  const [examUploading, setExamUploading] = useState(false);
  const [selectedExamFiles, setSelectedExamFiles] = useState({});
  const [examComments, setExamComments] = useState({});

  const getEntityId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value.id || value._id || null;
    }
    return value;
  };

  const isSameId = (left, right) => {
    const a = getEntityId(left);
    const b = getEntityId(right);
    return a !== null && b !== null && String(a) === String(b);
  };

  // Get student's enrolled courses
  const userId = user?.id || user?._id;
  const enrollments = userId
    ? (getStudentEnrollments(userId) || []).filter((enrollment) => enrollment.status !== 'cancelled')
    : [];
  const grades = userId ? (getStudentGrades(userId) || []) : [];
  const homeworkSubmissions = userId ? (getStudentHomeworkSubmissions(userId) || []) : [];

  const getCourseDetails = (courseId) => {
    return getCourseById(courseId);
  };

  const getModuleDetails = (course) => {
    return course?.module;
  };

  const getStudyYearDetails = (course) => {
    return course?.studyYear;
  };

  const getStudyDetails = (course) => {
    return course?.study;
  };

  const resolveStudyName = (course, studyYear) => {
    if (course?.study?.name) return course.study.name;

    const studyYearId = getEntityId(studyYear || course?.studyYear);
    if (!studyYearId) return '-';

    const matchedStudy = (studies || []).find((item) =>
      (item.studyYears || []).some((year) => String(getEntityId(year)) === String(studyYearId))
    );

    return matchedStudy?.name || '-';
  };

  const getStudyYearLabel = (studyYear) => {
    const number = studyYear?.yearNumber || studyYear?.year;
    if (number !== undefined && number !== null && number !== '') {
      return `Jaar ${number}`;
    }
    return studyYear?.name || 'Onbekend jaar';
  };

  const getDisplayEcts = (course) => {
    const moduleEcts = course?.module?.ects;
    if (moduleEcts !== undefined && moduleEcts !== null && moduleEcts !== '') {
      return moduleEcts;
    }

    const courseEcts = course?.ects;
    if (courseEcts !== undefined && courseEcts !== null && courseEcts !== '') {
      return courseEcts;
    }

    const modules = Array.isArray(course?.modules) ? course.modules : [];
    const summedModuleEcts = modules.reduce((total, item) => {
      const ects = Number(item?.ects);
      return Number.isFinite(ects) ? total + ects : total;
    }, 0);

    return summedModuleEcts > 0 ? summedModuleEcts : '-';
  };

  const formatFriendlyDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const formatFriendlyDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const revokePdfUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const openPdfModal = (url, title) => {
    setPdfModal({ isOpen: true, url, title });
  };

  // These files sit behind an authenticated endpoint, so a plain <iframe src>
  // can't fetch them (no Authorization header on a direct navigation). Fetch
  // the bytes ourselves and hand the modal a blob URL instead.
  const handleViewHomeworkAttachment = async (homeworkId, title) => {
    try {
      const { blob } = await homeworkAPI.fetchAttachment(homeworkId, 'view');
      openPdfModal(URL.createObjectURL(blob), title);
    } catch (error) {
      console.error('Fout bij laden van opdracht:', error);
      setHomeworkError(error.message || 'Opdracht openen mislukt');
    }
  };

  const handleViewHomeworkSubmission = async (homeworkId, submissionId, title) => {
    try {
      const { blob } = await homeworkAPI.fetchSubmissionFile(homeworkId, submissionId, 'view');
      openPdfModal(URL.createObjectURL(blob), title);
    } catch (error) {
      console.error('Fout bij laden van inzending:', error);
      setHomeworkError(error.message || 'Inzending openen mislukt');
    }
  };

  const getCourseGrades = (courseId) => {
    return grades.filter((g) => isSameId(g.courseId, courseId));
  };

  const getCourseHomework = (courseId) => {
    return (homework || []).filter((item) =>
      isSameId(item.courseId || item.course?.id || item.course?._id || item.course, courseId)
    );
  };

  const getCourseExams = (courseId) => {
    return (data?.schedules || [])
      .filter((event) => {
        const eventCourseId = String(event.courseId || event.course?.id || event.course?._id || event.course || '');
        const eventType = String(event.eventType || event.type || '');
        return eventCourseId === String(courseId) && EXAM_EVENT_TYPES.has(eventType);
      })
      .sort((a, b) => new Date(a.startDateTime || a.start || 0) - new Date(b.startDateTime || b.start || 0));
  };

  const calculateProgress = (courseId) => {
    const courseGrades = getCourseGrades(courseId);
    const courseHomework = getCourseHomework(courseId);
    
    if (courseGrades.length === 0 && courseHomework.length === 0) return 0;
    
    const totalItems = courseGrades.length + courseHomework.length;
    const completedItems = [
      ...courseGrades,
      ...homeworkSubmissions.filter((submission) =>
        submission.homeworkId && courseHomework.find((hw) => isSameId(hw.id || hw._id, submission.homeworkId))
      )
    ].filter((item) => item.status === 'graded' || item.status === 'passed').length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  useEffect(() => {
    const loadMaterials = async () => {
      if (!selectedCourse) {
        setCourseMaterials([]);
        setMaterialsError('');
        return;
      }

      // Pak het ID van de geselecteerde course
      const courseId = selectedCourse.id || selectedCourse._id;

      const moduleIds = [
        ...(selectedCourse.module ? [selectedCourse.module.id || selectedCourse.module._id || selectedCourse.module] : []),
        ...((selectedCourse.modules || []).map((mod) => mod.id || mod._id || mod))
      ].filter(Boolean);

      setMaterialsLoading(true);
      setMaterialsError('');
      try {
        // Bouw de parameters op. We sturen nu ALTIJD het courseId mee, 
        // en ALS er moduleIds zijn sturen we die ook mee.
        const params = { courseId };
        
        if (moduleIds.length > 0) {
          params.moduleIds = moduleIds.join(',');
        }

        const response = await moduleMaterialsAPI.getAll(params);
        setCourseMaterials(response.materials || []);
      } catch (error) {
        console.error("Fout bij laden materialen:", error);
        setMaterialsError(error.message || 'Materialen laden mislukt');
        setCourseMaterials([]);
      } finally {
        setMaterialsLoading(false);
      }
    };

    loadMaterials();
  }, [selectedCourse]);

  useEffect(() => {
    const loadHomeworkSubmissions = async () => {
      if (!selectedCourse) return;
      const selectedCourseId = getEntityId(selectedCourse);
      const normalizedCourseId = selectedCourseId ? String(selectedCourseId) : '';
      const assignments = (homework || []).filter((item) =>
        String(item.courseId || item.course?.id || item.course?._id || item.course || '') === normalizedCourseId
      );
      if (!assignments.length) return;

      for (const assignment of assignments) {
        const homeworkId = assignment?.id || assignment?._id;
        if (!homeworkId || homeworkSubmissionsMap[homeworkId]) continue;
        try {
          const submissions = await homeworkAPI.getSubmissions(homeworkId);
          setHomeworkSubmissionsMap((prev) => ({ ...prev, [homeworkId]: submissions || [] }));
        } catch (error) {
          console.error('Fout bij laden huiswerk inzendingen:', error);
        }
      }
    };

    loadHomeworkSubmissions();
  }, [selectedCourse, homework, homeworkSubmissionsMap]);

  useEffect(() => {
    return () => {
      revokePdfUrl(pdfModal.url);
    };
  }, [pdfModal.url]);

  useEffect(() => {
    const loadExamComponents = async () => {
      try {
        const response = await examComponentsAPI.getStudentPending();
        setExamComponentItems(response.items || []);
      } catch (error) {
        console.error('Fout bij laden examenonderdelen:', error);
      }
    };

    loadExamComponents();
  }, []);

  const handleExamComponentSubmit = async (scheduleEventId) => {
    const file = selectedExamFiles[scheduleEventId];
    if (!file) return;

    setExamUploading(true);
    try {
      await examComponentsAPI.submitStudentFile({
        scheduleEventId,
        file,
        comments: examComments[scheduleEventId] || ''
      });
      setSelectedExamFiles((prev) => ({ ...prev, [scheduleEventId]: null }));
      setExamComments((prev) => ({ ...prev, [scheduleEventId]: '' }));
      const response = await examComponentsAPI.getStudentPending();
      setExamComponentItems(response.items || []);
    } catch (error) {
      console.error('Fout bij indienen examenonderdeel:', error);
      alert(error.message || t('common.error'));
    } finally {
      setExamUploading(false);
    }
  };

  const handleHomeworkSubmit = async (homeworkId) => {
    const mode = homeworkSubmissionModes[homeworkId] || 'document';
    const file = homeworkSelectedFiles[homeworkId];
    const comment = homeworkComments[homeworkId];
    const textContent = homeworkTextSubmissions[homeworkId];

    const trimmedText = String(textContent || '').trim();
    if (mode === 'text' && !trimmedText) return;
    if (mode === 'document' && !file) return;

    setHomeworkUploading(true);
    setHomeworkError('');
    try {
      await homeworkAPI.submit(homeworkId, file, comment, mode === 'text' ? trimmedText : '');
      const submissions = await homeworkAPI.getSubmissions(homeworkId);
      setHomeworkSubmissionsMap((prev) => ({ ...prev, [homeworkId]: submissions || [] }));
      setHomeworkSelectedFiles((prev) => ({ ...prev, [homeworkId]: null }));
      setHomeworkComments((prev) => ({ ...prev, [homeworkId]: '' }));
      setHomeworkTextSubmissions((prev) => ({ ...prev, [homeworkId]: '' }));
    } catch (error) {
      console.error('Fout bij indienen huiswerk:', error);
      setHomeworkError(error.message || 'Huiswerk indienen mislukt');
    } finally {
      setHomeworkUploading(false);
    }
  };

  const renderCourseDetail = (course) => {
    if (!course) return null;
    
    const module = getModuleDetails(course);
    const studyYear = getStudyYearDetails(course);
    const study = getStudyDetails(course);
    const studyName = resolveStudyName(course, studyYear);
    const courseId = getEntityId(course);
    const courseGrades = getCourseGrades(courseId);
    const courseHomework = getCourseHomework(courseId);
    const courseExams = getCourseExams(courseId);
    const progress = calculateProgress(courseId);
    const displayEcts = getDisplayEcts(course);

    return (
      <div className="course-detail-view">
        <button className="back-button" onClick={() => setSelectedCourse(null)}>
          ← {t('common.back')}
        </button>

        <div className="detail-header">
          <h2>{course.name}</h2>
          <span className="course-code-badge">{course.code}</span>
        </div>

        <div className="detail-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('portal.dashboard')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'grades' ? 'active' : ''}`}
            onClick={() => setActiveTab('grades')}
          >
            {t('nav.grades')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'homework' ? 'active' : ''}`}
            onClick={() => setActiveTab('homework')}
          >
            {t('nav.homework')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'exams' ? 'active' : ''}`}
            onClick={() => setActiveTab('exams')}
          >
            {t('nav.exams')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
            onClick={() => setActiveTab('materials')}
          >
            {t('nav.materials')}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="progress-section">
                <h3>{t('progress.title')}</h3>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${progress}%` }}>
                    {progress}%
                  </div>
                </div>
              </div>

              <div className="course-hierarchy">
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('study.title')}:</span>
                  <span className="hierarchy-value">{studyName || study?.name || '-'}</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('studyYear.title')}:</span>
                  <span className="hierarchy-value">{studyYear?.year || studyYear?.yearNumber || studyYear?.name || '-'}</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('module.title')}:</span>
                  <span className="hierarchy-value">{module?.name ? `${module.name} (${module?.code || '-'})` : '-'}</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('module.ects')}:</span>
                  <span className="hierarchy-value">{displayEcts}</span>
                </div>
              </div>

              <div className="course-info-grid">
                <div className="info-card">
                  <h4>{t('course.type')}</h4>
                  <p>{t(`courseType.${course.courseType || 'instructor-led'}`)}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.duration')}</h4>
                  <p>{course.duration} {t('course.days')}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.location')}</h4>
                  <p>{translateLocation(t, course.location)}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.startDate')}</h4>
                  <p>{formatFriendlyDate(course.startDate)}</p>
                </div>
              </div>

              {course.courseType === 'instructor-led' && course.teamsUrl && (
                <div className="teams-section">
                  <h4>{t('course.teamsUrl')}</h4>
                  <a href={course.teamsUrl} target="_blank" rel="noopener noreferrer" className="teams-link">
                    📹 {t('exam.attend')} Teams Vergadering
                  </a>
                </div>
              )}

              {course.courseType === 'self-paced' && course.deadline && (
                <div className="deadline-section">
                  <h4>{t('course.deadline')}</h4>
                  <p className="deadline-date">{formatFriendlyDate(course.deadline)}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'grades' && (
            <div className="grades-tab">
              <h3>{t('grade.title')}</h3>
              {courseGrades.length > 0 ? (
                <div className="grades-list">
                  {courseGrades.map((grade, index) => (
                    <div key={index} className="grade-item">
                      <span className="grade-label">{t('grade.score')}:</span>
                      <span className={`grade-value ${grade.score >= 55 ? 'passed' : 'failed'}`}>
                        {grade.score}%
                      </span>
                      <span className={`grade-status ${grade.status}`}>
                        {t(`grade.${grade.status}`)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
              
              <div className="passing-threshold">
                <p>{t('grade.passingThreshold')}</p>
              </div>
            </div>
          )}

          {activeTab === 'homework' && (
            <div className="homework-tab">
              <h3>{t('nav.homework')}</h3>
              {homeworkError && <p className="no-data">{homeworkError}</p>}
              {courseHomework.length > 0 ? (
                <div className="homework-list">
                  {courseHomework.map((hw) => {
                    const homeworkId = hw.id || hw._id;
                    const submissions = homeworkSubmissionsMap[homeworkId] || [];
                    const latestSubmission = submissions[0];
                    const submission = latestSubmission || homeworkSubmissions.find((s) => isSameId(s.homeworkId, homeworkId));
                    return (
                      <div key={homeworkId} className="homework-item">
                        <div className="homework-header">
                          <h4>{hw.title}</h4>
                          <span className={`homework-status ${submission?.status || 'pending'}`}>
                            {submission?.status === 'submitted' ? t('homework.submitted') : t('common.pending')}
                          </span>
                        </div>
                        <p className="homework-description">{hw.description}</p>
                        <div className="homework-meta">
                          <span>{t('homework.dueDate')}: {formatFriendlyDate(hw.dueDate)}</span>
                          <span>{t('homework.maxScore')}: {hw.maxScore ?? '-'}</span>
                        </div>
                        {hw.contentType === 'text' && hw.content && (
                          <div className="submission-result">
                            <p className="feedback">{hw.content}</p>
                          </div>
                        )}
                        {hw.contentType !== 'text' && hw.attachment?.originalName && (
                          <div className="submission-result">
                            <button
                              type="button"
                              className="material-link-action"
                              onClick={() => handleViewHomeworkAttachment(homeworkId, hw.title)}
                            >
                              {t('homework.viewAssignment')} - {hw.attachment.originalName}
                            </button>
                          </div>
                        )}
                        {submission && (
                          <div className="submission-result">
                            <span>{t('homework.grade')}: {submission.grade}</span>
                            {submission.feedback && (
                              <p className="feedback">{t('homework.feedback')}: {submission.feedback}</p>
                            )}
                            {submission.id && (
                              <button
                                type="button"
                                className="material-link-action"
                                onClick={() => handleViewHomeworkSubmission(homeworkId, submission.id, `${hw.title} - ${t('homework.mySubmission')}`)}
                              >
                                {t('homework.viewSubmission')}
                              </button>
                            )}
                          </div>
                        )}
                        <div className="homework-actions">
                          <div className="submission-mode-toggle">
                            <label>
                              <input
                                type="radio"
                                name={`my-courses-submission-mode-${homeworkId}`}
                                checked={(homeworkSubmissionModes[homeworkId] || 'document') === 'document'}
                                onChange={() => setHomeworkSubmissionModes((prev) => ({ ...prev, [homeworkId]: 'document' }))}
                              />
                              Document
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`my-courses-submission-mode-${homeworkId}`}
                                checked={(homeworkSubmissionModes[homeworkId] || 'document') === 'text'}
                                onChange={() => setHomeworkSubmissionModes((prev) => ({ ...prev, [homeworkId]: 'text' }))}
                              />
                              Tekst
                            </label>
                          </div>

                          {(homeworkSubmissionModes[homeworkId] || 'document') === 'document' && (
                            <label className="file-upload">
                              <input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setHomeworkSelectedFiles((prev) => ({ ...prev, [homeworkId]: e.target.files?.[0] || null }))}
                              />
                              {t('homework.selectFile')}
                            </label>
                          )}

                          {(homeworkSubmissionModes[homeworkId] || 'document') === 'text' && (
                            <textarea
                              className="comment-input"
                              placeholder="Typ je tekstinzending hier"
                              value={homeworkTextSubmissions[homeworkId] || ''}
                              onChange={(e) => setHomeworkTextSubmissions((prev) => ({ ...prev, [homeworkId]: e.target.value }))}
                            />
                          )}

                          <textarea
                            className="comment-input"
                            placeholder={t('homework.commentsPlaceholder')}
                            value={homeworkComments[homeworkId] || ''}
                            onChange={(e) => setHomeworkComments((prev) => ({ ...prev, [homeworkId]: e.target.value }))}
                          />

                          <button
                            type="button"
                            className="upload-btn"
                            onClick={() => handleHomeworkSubmit(homeworkId)}
                            disabled={
                              homeworkUploading ||
                              (
                                ((homeworkSubmissionModes[homeworkId] || 'document') === 'document')
                                  ? !homeworkSelectedFiles[homeworkId]
                                  : !String(homeworkTextSubmissions[homeworkId] || '').trim()
                              )
                            }
                          >
                            {homeworkUploading ? t('common.loading') : t('homework.uploadFile')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}

          {activeTab === 'exams' && (
            <div className="exams-tab">
              <h3>{t('nav.exams')}</h3>
              {courseExams.length > 0 ? (
                <div className="exams-list">
                  {courseExams.map((exam) => {
                    const grade = courseGrades.find((g) => g.examType === exam.examType);
                    const isUploadType = EXAM_UPLOAD_TYPES.has(exam.examType);
                    const scheduleEventId = String(exam.id);
                    const componentItem = isUploadType
                      ? examComponentItems.find((item) => String(item.scheduleEventId) === scheduleEventId)
                      : null;
                    const alreadySubmitted = Boolean(componentItem?.submitted);
                    const canUploadNow = Boolean(componentItem?.canUploadNow ?? componentItem?.canUploadToday) && !alreadySubmitted;

                    return (
                      <div key={scheduleEventId} className="exam-item">
                        <div className="exam-header">
                          <h4>{exam.title}</h4>
                          <span className="exam-type">{examTypeLabel(exam.examType)}</span>
                        </div>
                        <div className="exam-details">
                          <span>{t('exam.date')}: {formatFriendlyDate(exam.startDateTime || exam.start)}</span>
                          {exam.location && <span>{t('exam.location')}: {translateLocation(t, exam.location)}</span>}
                          {exam.mode === 'online' && exam.meetingUrl && (
                            <a href={exam.meetingUrl} target="_blank" rel="noopener noreferrer">Online link</a>
                          )}
                        </div>

                        {grade && (
                          <div className="exam-grade">
                            <span className={`grade ${grade.score >= 55 ? 'passed' : 'failed'}`}>
                              {grade.score}% - {t(`grade.${grade.status}`)}
                            </span>
                          </div>
                        )}

                        {isUploadType && (
                          <div className="submission-result">
                            {alreadySubmitted ? (
                              <>
                                <span>Geupload op: {formatFriendlyDate(componentItem.submitted.submittedAt)}</span>
                                {componentItem.submitted.file?.url && (
                                  <button
                                    type="button"
                                    className="material-link-action"
                                    onClick={() => window.open(componentItem.submitted.file.url, '_blank', 'noopener,noreferrer')}
                                  >
                                    Bekijk upload
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <label className="file-upload">
                                  <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    disabled={!canUploadNow}
                                    onChange={(e) => setSelectedExamFiles((prev) => ({ ...prev, [scheduleEventId]: e.target.files?.[0] || null }))}
                                  />
                                  {t('homework.selectFile')}
                                </label>
                                <button
                                  type="button"
                                  className="upload-btn"
                                  disabled={examUploading || !canUploadNow || !selectedExamFiles[scheduleEventId]}
                                  onClick={() => handleExamComponentSubmit(scheduleEventId)}
                                >
                                  {examUploading ? t('common.loading') : 'Upload examenonderdeel'}
                                </button>
                                {!canUploadNow && (
                                  <p className="no-data">
                                    {componentItem?.uploadWindowStatus === 'scheduled'
                                      ? 'Uploaden is nog niet beschikbaar voor dit evenement.'
                                      : 'De uploaddeadline voor dit evenement is verstreken.'}
                                  </p>
                                )}
                                <p className="no-data">
                                  Uploadperiode: {formatFriendlyDateTime(componentItem?.uploadAvailableFrom || componentItem?.createdAt)} t/m {formatFriendlyDateTime(componentItem?.uploadAvailableUntil || componentItem?.endDateTime)}
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="materials-tab">
              <h3>{t('nav.materials')}</h3>
              {materialsError && <p className="no-data">{materialsError}</p>}
              {materialsLoading && <p>{t('common.loading')}</p>}
              {!materialsLoading && courseMaterials.length > 0 ? (
                <div className="materials-list">
                  {courseMaterials.map((material) => (
                    <div key={material.id} className="material-item material-item-rich">
                      <span className="material-icon">
                        {material.type === 'media' ? '🎬' : material.type === 'document' ? '📄' : material.type === 'article' ? '📝' : '🔗'}
                      </span>
                      <div className="material-body">
                        <p className="material-link">{material.title}</p>
                        {material.description && <p className="material-description">{material.description}</p>}
                        {material.type === 'article' && material.articleContent && (
                          <div className="material-article" dangerouslySetInnerHTML={{ __html: material.articleContent }} />
                        )}
                        <div className="material-links">
                          {material.externalUrl && (
                            <a href={material.externalUrl} target="_blank" rel="noopener noreferrer" className="material-link-action">
                              Open link
                            </a>
                          )}
                          {material.file?.filename && (
                            <>
                              <button
                                type="button"
                                className="material-link-action"
                                onClick={async () => {
                                  try {
                                    setMaterialsError('');
                                    const { blob, contentType, filename } = await moduleMaterialsAPI.fetchFile(material.id, 'view');
                                    const effectiveName = filename || material.file?.originalName || material.file?.filename || material.title || 'bestand';
                                    const isPdf = /pdf/i.test(contentType || '') || /\.pdf$/i.test(effectiveName);

                                    if (!isPdf) {
                                      downloadBlob(blob, effectiveName);
                                      return;
                                    }

                                    revokePdfUrl(pdfModal.url);
                                    const objectUrl = URL.createObjectURL(blob);
                                    setPdfModal({
                                      isOpen: true,
                                      url: objectUrl,
                                      title: material.title || 'Materiaal bekijken'
                                    });
                                  } catch (error) {
                                    console.error('Fout bij openen van materiaal:', error);
                                    setMaterialsError(error.message || 'Materiaal openen mislukt');
                                  }
                                }}
                              >
                                Bekijken
                              </button>
                              <button
                                type="button"
                                className="material-link-action"
                                onClick={async () => {
                                  try {
                                    setMaterialsError('');
                                    const { blob, filename } = await moduleMaterialsAPI.fetchFile(material.id, 'download');
                                    downloadBlob(
                                      blob,
                                      filename || material.file?.originalName || material.file?.filename || material.title || 'bestand'
                                    );
                                  } catch (error) {
                                    console.error('Fout bij downloaden van materiaal:', error);
                                    setMaterialsError(error.message || 'Materiaal downloaden mislukt');
                                  }
                                }}
                              >
                                Downloaden
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="material-type">{material.type}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}
        </div>

        <PDFViewerModal
          isOpen={pdfModal.isOpen}
          onClose={() => {
            revokePdfUrl(pdfModal.url);
            setPdfModal({ isOpen: false, url: '', title: '' });
          }}
          pdfUrl={pdfModal.url}
          title={pdfModal.title}
        />
      </div>
    );
  };

  if (selectedCourse) {
    return (
      <div className="my-courses-page">
        {renderCourseDetail(selectedCourse)}
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      <div className="page-header">
        <h1>{t('portal.myCourses')}</h1>
      </div>

      <div className="courses-grid">
        {Object.entries(enrollments.reduce((groups, enrollment) => {
          const enrollmentCourseId = getEntityId(enrollment.courseId);
          const course = getCourseDetails(enrollmentCourseId) || enrollment.course || null;
          if (!course) return groups;

          const studyYear = getStudyYearDetails(course);
          const key = getStudyYearLabel(studyYear);
          if (!groups[key]) groups[key] = [];
          groups[key].push({ enrollment, course, studyYear, enrollmentCourseId });
          return groups;
        }, {})).sort(([a], [b]) => a.localeCompare(b, 'nl', { numeric: true })).map(([yearLabel, yearItems]) => (
          <div key={yearLabel} className="year-group-block">
            <div className="year-group-header">
              <h2>{yearLabel}</h2>
              <span>{yearItems.length} vak(ken)</span>
            </div>
            <div className="year-group-grid">
              {yearItems.map(({ enrollment, course, studyYear, enrollmentCourseId }) => {
                const module = getModuleDetails(course);
                const studyName = resolveStudyName(course, studyYear);
                const progress = calculateProgress(enrollmentCourseId);
                const displayEcts = getDisplayEcts(course);

                return (
                  <div
                    key={enrollment.id || enrollment._id || enrollmentCourseId}
                    className="course-detail-card"
                    onClick={() => setSelectedCourse(course)}
                  >
                    <div className="course-badge">
                      {enrollment.status === 'active' ? t('enrollment.active') : enrollment.status}
                    </div>

                    <h3 className="course-title">{course?.name}</h3>
                    <p className="course-code">{course?.code}</p>

                    <div className="course-info">
                      <div className="info-row">
                        <span className="info-label">{t('study.title')}:</span>
                        <span className="info-value">{studyName}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('studyYear.title')}:</span>
                        <span className="info-value">{studyYear?.year || studyYear?.yearNumber || studyYear?.name || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('module.title')}:</span>
                        <span className="info-value">{module?.name || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('course.duration')}:</span>
                        <span className="info-value">{course?.duration} {t('course.days')}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('course.location')}:</span>
                        <span className="info-value">{translateLocation(t, course?.location)}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('module.ects')}:</span>
                        <span className="info-value">{displayEcts}</span>
                      </div>
                    </div>

                    <div className="course-progress">
                      <div className="progress-label">
                  <span>{t('progress.title')}</span>
                  <span>{progress}%</span>
                </div>
                <div className="progress-bar-small">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              </div>

              <div className="course-type-badge">
                {course?.courseType === 'self-paced' ? '📚 Zelfstudie' : '👨‍🏫 Docentgeleid'}
              </div>
            </div>
          );
              })}
            </div>
          </div>
        ))}
        
        {enrollments.length === 0 && (
          <div className="no-courses">
            <p>{t('portal.noCourses')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
